/**
 * `crypto.randomUUID` only exists in Chromium 92+ and Safari 15.4+, so older Android WebViews and
 * iOS versions throw `crypto.randomUUID is not a function` during render and blank the whole app.
 * The polyfill is built on `getRandomValues` (much older support) rather than `Math.random`
 * because several callers use the value as OAuth `state`, which must be unguessable.
 *
 * Remove (along with its import in `index.tsx`) once Clients.App's supported-browser floor is
 * Chromium 92+ and Safari 15.4+ everywhere it runs, including Android WebView and iOS WKWebView.
 * Note the secure-context caveat in `browserApis.ts` — `randomUUID` is `undefined` on a non-secure
 * origin even on a current browser.
 */
export function randomUUIDPolyfill(): `${string}-${string}-${string}-${string}-${string}` {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    // RFC 4122 §4.4: version 4 in the high nibble of byte 6, variant 10xx in byte 8.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function installCryptoRandomUUID(): void {
    if (
        typeof globalThis.crypto?.randomUUID !== "function" &&
        typeof globalThis.crypto?.getRandomValues === "function"
    ) {
        globalThis.crypto.randomUUID = randomUUIDPolyfill;
    }
}

installCryptoRandomUUID();
